import { Injectable } from '@angular/core';

import { Subject } from 'rxjs';

import { TwcPopoverComponent, TwcToolbarComponent } from '@topcon/twc-angular';

import { OptionToolbarData } from '@mirage-projects/cw-commons';

type triggerActions = 'show' | 'hide' | 'redraw';

/**
 * Service with the logic to manage the toolbar
 */
@Injectable({
    providedIn: 'root',
})
export class ToolbarService {
    private twcToolbars: TwcToolbarComponent[] = [];
    private popovers: TwcPopoverComponent[] = [];
    private readonly closePopoversSubject$: Subject<void> = new Subject();
    readonly closePopovers$ = this.closePopoversSubject$.asObservable();
    private readonly selectOptionSubject$: Subject<OptionToolbarData> = new Subject();
    readonly selectOption$ = this.selectOptionSubject$.asObservable();

    private readonly panelVisibilitySubject$: Subject<[panelName: string, action: triggerActions]> = new Subject();
    readonly panelVisibility$ = this.panelVisibilitySubject$.asObservable();

    private readonly panelStatusChangeSubject$: Subject<[panelName: string, status: boolean]> = new Subject();
    readonly panelStatusChange$ = this.panelStatusChangeSubject$.asObservable();

    private isChangeConfirmed: () => Promise<boolean> = () => Promise.resolve(true);

    selectOption(option: OptionToolbarData): void {
        this.selectOptionSubject$.next(option);
    }

    /**
     * Selects toolbar option and shows the tools panel, if it's enabled in configuration
     * @param option Options to select the tool in the panel
     */
    async selectOptionAndShowToolPanel(option: OptionToolbarData): Promise<void> {
        if (await this.isChangeConfirmed()) {
            this.selectOptionSubject$.next({ ...option, showToolsPanel: true });
        }
    }

    showPanel(panelName: string): void {
        this.panelVisibilitySubject$.next([panelName, 'show']);
    }

    hidePanel(panelName: string): void {
        this.panelVisibilitySubject$.next([panelName, 'hide']);
    }

    redrawPanel(panelName: string): void {
        this.panelVisibilitySubject$.next([panelName, 'redraw']);
    }

    // this function is used internally from the toolbar component to emit the changes from the popovers
    emitStatusChange(panelName: string, status: boolean): void {
        this.panelStatusChangeSubject$.next([panelName, status]);
    }

    /** This method stores the twcToolbar in the twcToolbars array */
    registerToolbar(twcToolbar: TwcToolbarComponent): void {
        this.twcToolbars.push(twcToolbar);
    }

    /** This method removes the twcToolbar from twcToolbars array */
    removeRegisterToolbar(twcToolbar: TwcToolbarComponent): void {
        this.twcToolbars = this.twcToolbars.filter((twcToolbarAux) => twcToolbarAux !== twcToolbar);
    }

    /** This function collpases all toolbars exept the toolbar twcToolbar. It is used to collapse the expanded toolbars whuen you click on one */
    collapseOthersToolbars(twcToolbar: TwcToolbarComponent, forceAll = false): void {
        this.twcToolbars.forEach((toolbar) => {
            if (twcToolbar !== toolbar || forceAll) {
                toolbar.collapseDescendants();
            }
        });
        this.closePopoversSubject$.next();
    }

    /**
     * Sets a handler that asks the user to confirm leaving the panel when there are unsaved changes.
     * The handler must return a promise resolving to true if the user confirms, or false otherwise.
     */
    setChangeConfirmation(isChangeConfirmed: () => Promise<boolean>): void {
        this.isChangeConfirmed = isChangeConfirmed;
    }
}
